import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import {
  convertGeometryToMultiPolygon,
  convertLatLngPathToGeometry, removeGeometryPolygonIntersections,
} from '~/_shared/types/polygon/polygon.utils';
import { noop } from '~/_shared/utils/function.helpers';
import { computeLatLngBoundsArea } from '~/_shared/utils/gis/boundingBox.helpers';
import { createLatLngBoundsFromPolygonPaths } from '~/_shared/utils/map/latLngBounds.factory';
import type { MapShapeInstance } from '~/map/map/mapObjects/mapShape/mapShapeModel';
import { ModalType } from '~/modal/modalType.enum';
import { useModal } from '~/modal/useModal.hook';
import { boundaryDrawCloseModal } from '~/store/frontendState/mapTools/boundary/boundaryDraw/boundaryDraw.actionCreators';
import { useBoundaryPolygonEditActiveSelector } from '~/store/frontendState/mapTools/boundary/boundaryDraw/boundaryDraw.selectors';
import { updateBoundaryPolygonPaths } from '~/store/frontendState/mapTools/boundary/boundaryPolygonUpdate/boundaryPolygonUpdate.actionCreators';
import { BoundaryEditManualMethod } from '../boundaryEditModal/boundaryEditModal.helpers';

export const useBoundaryPolygonEditSubmit = () => {
  const dispatch = useDispatch();
  const { openModal: openBoundaryEditSubmitModal } = useModal(ModalType.BoundaryEditSubmit);
  const drawState = useBoundaryPolygonEditActiveSelector();

  const dragSubmit = useCallback(() => {
    if (!drawState) {
      return;
    }

    dispatch(boundaryDrawCloseModal());

    const polygonsWithoutIntersections = {
      type: 'GeometryCollection' as const,
      geometries: getGeometryWithoutIntersections(drawState.mapShapes).flatMap(geometry => geometry.value),
    };
    const multiPolygon = convertGeometryToMultiPolygon(polygonsWithoutIntersections).map(({ path }) => {
      const bounds = createLatLngBoundsFromPolygonPaths(path);

      return ({
        path,
        holes: [],
        bounds,
        boundsArea: bounds && computeLatLngBoundsArea(bounds),
      });
    });

    dispatch(updateBoundaryPolygonPaths(
      drawState.drawModeData.boundaryId,
      drawState.boundaryGroupId,
      multiPolygon));
  }, [dispatch, drawState]);

  const openBoundaryDrawSubmitModal = useCallback(() => {
    if (!drawState) {
      return;
    }

    const newShapes = drawState.mapShapes.filter(shape => !drawState.initialShapes.find(initialSpape => initialSpape.id === shape.id));
    const existingShapes = drawState.mapShapes.filter(shape => drawState.initialShapes.find(initialSpape => initialSpape.id === shape.id));

    const geometry = getGeometryWithoutIntersections(newShapes);
    const baseGeometry = getGeometryWithoutIntersections(existingShapes);

    dispatch(boundaryDrawCloseModal());

    openBoundaryEditSubmitModal({
      method: BoundaryEditManualMethod.Draw,
      geometry,
      baseGeometry,
      editedBoundaryTerritoryGroupId: drawState.drawModeData.boundaryTerritoryGroupId,
      selectedBoundaryId: drawState.drawModeData.boundaryId,
      isBoundarySelectDisabled: true,
      hideEditAnother: true,
      onSubmit: noop,
    });
  }, [drawState, dispatch, openBoundaryEditSubmitModal]);

  const submitBoundaryPolygonEdit = useCallback(() => {
    if (!drawState) {
      return;
    }

    const hasNewAreas = drawState.mapShapes
      .some(shape => !drawState.initialShapes.find(initialSpape => initialSpape.id === shape.id));

    if (hasNewAreas) {
      openBoundaryDrawSubmitModal();
    }
    else {
      dragSubmit();
    }
  }, [dragSubmit, drawState, openBoundaryDrawSubmitModal]);

  return { submitBoundaryPolygonEdit };
};

const getGeometryWithoutIntersections = (mapShapes: readonly MapShapeInstance[]) => {
  const polygonsWithoutIntersections = mapShapes.flatMap(shape =>
    removeGeometryPolygonIntersections(convertLatLngPathToGeometry(shape.outlines)).features);

  return polygonsWithoutIntersections.map(feature => ({
    type: 'geometry' as const,
    value: feature.geometry,
  }));
};
